import type { DateSelectArg, EventClickArg, EventDropArg } from '@fullcalendar/core';
import interactionPlugin from '@fullcalendar/interaction';
import type { EventResizeDoneArg } from '@fullcalendar/interaction';
import luxon3Plugin from '@fullcalendar/luxon3';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { Avatar, Box, Skeleton, Typography, alpha, useTheme } from '@mui/material';
import React, { useRef } from 'react';

import { colorForUser } from './userColor.js';

const PLUGINS = [resourceTimelinePlugin, interactionPlugin, luxon3Plugin];
const DEFAULT_TIMEZONE = 'America/New_York';

interface RosterCalendarProps {
  monitors: { id: string; name: string; picture?: string }[];
  monitorsLoading: boolean;
  shifts: { id: string; userId: string; start: string; end: string; isRecurring: boolean; ruleId: string | null }[];
  onSelect: (arg: DateSelectArg) => void;
  onEventClick?: (arg: EventClickArg) => void;
  onEventDrop?: (arg: EventDropArg) => void;
  onEventResize?: (arg: EventResizeDoneArg) => void;
  onDatesSet?: (start: string, end: string) => void;
}

const RosterCalendar = ({
  monitors,
  monitorsLoading,
  shifts,
  onSelect,
  onEventClick,
  onEventDrop,
  onEventResize,
  onDatesSet,
}: RosterCalendarProps) => {
  const calendarRef = useRef<FullCalendar>(null);
  const theme = useTheme();

  const resources = monitors.map((m) => ({ id: m.id, title: m.name, extendedProps: { picture: m.picture } }));
  const events = shifts.map((s) => ({
    id: s.id,
    resourceId: s.userId,
    start: s.start,
    end: s.end,
    title: '',
    backgroundColor: colorForUser(s.userId),
    borderColor: colorForUser(s.userId),
    extendedProps: {
      isRecurring: s.isRecurring,
      occurrenceId: s.id,
      ruleId: s.ruleId ?? null,
    },
  }));

  if (monitorsLoading) {
    return (
      <Box>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} height={44} width={140} animation="wave" sx={{ mb: 0.5 }} />
        ))}
      </Box>
    );
  }

  if (!monitorsLoading && monitors.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1">No monitors</Typography>
        <Typography variant="body2" color="text.secondary">
          Add monitors in the Monitors tab to start scheduling shifts.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        // FullCalendar ships its own CSS and ignores the MUI theme, so on the
        // dark palette it renders as a transparent grid (black with light
        // borders). Drive its CSS variables from the theme so it is readable in
        // both light and dark mode.
        '& .fc': {
          '--fc-border-color': theme.palette.divider,
          '--fc-page-bg-color': theme.palette.background.paper,
          '--fc-neutral-bg-color': theme.palette.action.hover,
          '--fc-today-bg-color': alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.1),
          '--fc-now-indicator-color': theme.palette.error.main,
          '--fc-button-bg-color': theme.palette.primary.main,
          '--fc-button-border-color': theme.palette.primary.main,
          '--fc-button-hover-bg-color': theme.palette.primary.dark,
          '--fc-button-hover-border-color': theme.palette.primary.dark,
          '--fc-button-active-bg-color': theme.palette.primary.dark,
          '--fc-button-active-border-color': theme.palette.primary.dark,
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.background.paper,
          borderRadius: `${theme.shape.borderRadius}px`,
          padding: theme.spacing(1),
        },
        '& .fc .fc-toolbar-title': { fontSize: theme.typography.h6.fontSize },
        '& .fc-theme-standard td, & .fc-theme-standard th': { borderColor: theme.palette.divider },
        '& .fc .fc-datagrid-cell-cushion, & .fc .fc-col-header-cell-cushion, & .fc .fc-timeline-slot-cushion': {
          color: theme.palette.text.primary,
        },
        // Let the avatar+name cell use the full row height.
        '& .fc .fc-datagrid-cell-cushion': { width: '100%' },
      }}
    >
      <FullCalendar
        ref={calendarRef}
        plugins={PLUGINS}
        initialView="resourceTimelineWeek"
        // Overview -> detail ladder: Day is 30-min precise; Week uses 4-hour
        // blocks (6/day) with hourly drag-snap so shifts can be placed by time
        // without a full hourly week (~8000px scroll); Month stays day-columns.
        views={{
          resourceTimelineDay: { buttonText: 'Day', slotDuration: '00:30:00', slotLabelInterval: '02:00:00' },
          resourceTimelineWeek: {
            buttonText: 'Week',
            slotDuration: { hours: 4 },
            snapDuration: { hours: 1 },
            slotLabelInterval: { hours: 4 },
          },
          resourceTimelineMonth: { buttonText: 'Month', slotDuration: { days: 1 } },
        }}
        headerToolbar={{
          left: 'title',
          right: 'resourceTimelineDay,resourceTimelineWeek,resourceTimelineMonth today prev,next',
        }}
        buttonText={{ today: 'Today' }}
        resources={resources}
        events={events}
        // height="auto" sizes the calendar to its content so each monitor lane
        // takes a natural height — without it the last row expands to fill the
        // viewport.
        height="auto"
        resourceAreaWidth="220px"
        resourceAreaColumns={[
          {
            field: 'title',
            headerContent: 'Monitor',
            // Avatar + name, ringed in the monitor's identity color — the same
            // color their shift blocks use, so the grid ties back to a face.
            cellContent: (arg) => {
              const id = arg.resource?.id ?? '';
              const name = arg.resource?.title ?? '';
              const picture = arg.resource?.extendedProps?.picture as string | undefined;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Avatar
                    src={picture}
                    alt={name}
                    sx={{ width: 26, height: 26, fontSize: 12, border: `2px solid ${colorForUser(id)}` }}
                  >
                    {name.charAt(0)}
                  </Avatar>
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {name}
                  </Box>
                </Box>
              );
            },
          },
        ]}
        slotMinWidth={48}
        selectable
        selectMirror
        editable={true}
        allDaySlot={false}
        displayEventEnd
        nowIndicator
        timeZone={DEFAULT_TIMEZONE}
        eventTextColor="#ffffff"
        // Show the shift's local time range inside each block (the lane already
        // identifies the monitor). Ellipsis keeps narrow blocks tidy.
        // Recurring blocks show the Autorenew glyph before the time text.
        eventContent={(arg) => (
          <Box
            sx={{
              px: 0.75,
              py: 0.5,
              minHeight: 24,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: 12,
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {arg.event.extendedProps.isRecurring && (
              <AutorenewIcon sx={{ fontSize: 10, flexShrink: 0 }} />
            )}
            {arg.timeText}
          </Box>
        )}
        select={onSelect}
        eventClick={onEventClick}
        eventDrop={onEventDrop}
        eventResize={onEventResize}
        datesSet={(arg) => onDatesSet?.(arg.startStr, arg.endStr)}
      />
    </Box>
  );
};

export default RosterCalendar;
